<template>
  <Head>
    <title>{{ pageTitle }}</title>
    <body class="site--snow" />
    <meta name="description" :content="$t('page.snow.description')" />
    <meta name="og:title" :content="pageTitle" />
    <meta name="og:description" :content="$t('page.snow.description')" />
  </Head>
  <AdUnit unit="1" />
  <div class="page--snow" :class="loading ? 'loading' : ''">
    <div class="main">
      <div class="section">
        <h1 class="section__title">Snow Totals</h1>
        <AddressSetLocation
          :getLocationOnLoad="!hasLocation"
          v-model="location"
          @error="loading = false"
        />
        <div class="topline-readings">
          <div class="reading" v-if="loading || snowDepth !== null">
            <div class="reading__value">{{ snowDepth || 0 }} in</div>
            <div class="reading__label">Snow depth</div>
          </div>
          <div
            class="reading main-reading"
            v-if="
              loading ||
              (snowAccumulation !== null && 'inches' in snowAccumulation)
            "
          >
            <div class="reading__value">
              {{ (snowAccumulation && snowAccumulation.inches) || 0 }} in
            </div>
            <div class="reading__label">
              In the last
              {{ (snowAccumulation && snowAccumulation.hours) || 24 }} hours
            </div>
          </div>
          <div
            class="reading"
            v-if="
              forecast !== null && forecast.high24 === 0 && forecast.high48 > 0
            "
          >
            <div class="reading__value">
              <span
                v-if="forecast !== null && forecast.low48 !== forecast.high48"
                >{{ forecast.low48 }}&ndash;</span
              >{{ (forecast && forecast.high48) || 0 }} in
            </div>
            <div class="reading__label">2 day forecast</div>
          </div>
          <div class="reading" v-else-if="forecast !== null || loading">
            <div class="reading__value">
              <span
                v-if="forecast !== null && forecast.low24 !== forecast.high24"
                >{{ forecast.low24 }}&ndash;</span
              >{{ (forecast && forecast.high24) || 0 }} in
            </div>
            <div class="reading__label">24 hour forecast</div>
          </div>
        </div>
        <div v-if="!loading && readings.length === 0 && forecast === null">
          <p>
            Snowfall totals, snow depth, and snow forecasts could not be loaded
            for your location. This site currently only works within the United
            States.
          </p>
        </div>
        <div class="spinner" v-if="loading">
          <div class="double-bounce1"></div>
          <div class="double-bounce2"></div>
        </div>
      </div>
      <SnowMap
        v-if="readings.length > 0"
        :readings="readings"
        :center="[latLng.longitude, latLng.latitude]"
        @markerMove="markerMove"
      />
      <AdUnit unit="2" />
      <StationReadings :v-if="!loading" :readings="readings" />
      <Wikipedia
        v-if="$route && $route.query && $route.query.title"
        :articleTitle="$route.query.title"
        :showTitle="true"
      />
      <AdUnit unit="3" />
    </div>
    <TheFooter :elevation="true" :snow="false">
      <p>
        Weather websites are very good at reporting how much snow is forecast
        for the next day or week, but often make it difficult to see what the
        actual snowfall was at the end of the storm. This site attempts to
        correct that by combining and simplifying data from the National Weather
        Service and the NOAA.
      </p>
      <p>
        This site pulls data from multiple different sources of data from the
        <a href="https://www.weather.gov/">National Weather Service</a> and the
        <a href="https://www.nohrsc.noaa.gov"
          >National Weather Service NOHRSC</a
        >
        to create the easiest way to find the most accurate snowfall data in
        your area. The data is updated throughout the day as station readings
        are reported, usually no more than once an hour.
      </p>

      <p>
        You can view the snow accumulation, snow depth, and snowfall for your
        recent winter storms as well as nearby snow reports from weather
        stations across the country. You can also view the snowfall forecast for
        the next two days, and see a map of the recent snowfall in your area.
        You can use your current location to quickly get a sense of the current
        snowfall in your area or you can search for any address or city to see
        the snowfall in that area.
      </p>
      <template v-slot:nav>
        <li>
          <router-link :to="{ name: 'snowTotals' }">{{
            $t("page.snow.title")
          }}</router-link>
        </li>
        <li>
          <router-link :to="{ name: 'snowAtlas' }"
            >{{ $t("page.snow.title") }} Atlas</router-link
          >
        </li>
      </template>
    </TheFooter>
  </div>
</template>

<script>
import axios from "axios";
import { round } from "@/helpers";
import AddressSetLocation from "@/components/AddressSetLocation.vue";
import StationReadings from "@/components/StationReadings.vue";
import TheFooter from "@/components/TheFooter.vue";
import { Head } from "@vueuse/head";
import Wikipedia from "@/components/Wikipedia.vue";
import AdUnit from "@/components/AdUnit.vue";
import { defineAsyncComponent } from "vue";

export default {
  components: {
    AddressSetLocation,
    StationReadings,
    TheFooter,
    Head,
    Wikipedia,
    AdUnit,
    SnowMap: defineAsyncComponent(() => import("@/components/SnowMap.vue"))
  },
  data() {
    return {
      loading: true,
      setLocationOpen: false,
      location: {},
      forecast: null,
      readings: []
    };
  },
  computed: {
    hasLocation() {
      return (
        (this.$route.query.lat && this.$route.query.lng) ||
        this.$store.getters.hasLocation
      );
    },
    snowDepth() {
      const closestReading = this.readings.find(
        reading => typeof reading.snow_depth_inches !== "undefined"
      );
      if (closestReading && closestReading.distance < 20) {
        return round(closestReading.snow_depth_inches, 1);
      }
      return null;
    },
    snowAccumulation() {
      const closestReading = this.readings.find(
        reading => typeof reading.snowfall_inches !== "undefined"
      );
      if (closestReading && closestReading.distance < 20) {
        return {
          inches: round(closestReading.snowfall_inches, 1),
          hours: closestReading.snowfall_duration_hours
        };
      }
      return null;
    },
    latLng() {
      if (this.location && this.location.point) {
        return {
          latitude: this.location.point.coordinates[0],
          longitude: this.location.point.coordinates[1]
        };
      }
      return null;
    },
    pageTitle() {
      if (this.$route.query.title) {
        return `${decodeURI(this.$route.query.title)} ${this.$t(
          "page.snow.subpage-title"
        )}`;
      }
      return `${this.$t("page.snow.title")} - ${this.$t("page.snow.subtitle")}`;
    }
  },
  methods: {
    updateLocationFromRoute(route, isCurrent = false) {
      if (!route.query.lat || !route.query.lng) {
        return;
      }
      let name = "Saved location";
      if (isCurrent) {
        name = this.location.name ? this.location.name : "Current location";
      }
      if (route.query.title) {
        name = decodeURI(route.query.title);
      }
      this.location = {
        name,
        point: {
          coordinates: [route.query.lat, route.query.lng]
        }
      };
    },
    updateRoute() {
      if (typeof this.location === "undefined") {
        return;
      }
      const query = {
        lat: round(this.location.point.coordinates[0], 5),
        lng: round(this.location.point.coordinates[1], 5)
      };
      if (
        !["Current location", "Saved location", "Map location"].includes(
          this.location.name
        )
      ) {
        query.title = encodeURI(this.location.name);
      }
      this.$router.push({ query, hash: this.$route.hash });
    },
    routeLocation(location) {
      this.$router.push({
        name: "snowTotalsLocation",
        params: {
          latitude: `${location.coords.latitude}`,
          longitude: `${location.coords.longitude}`
        }
      });
    },
    locationError(error) {
      console.warn("location access denied", error);
      this.loading = false;
      this.setLocationOpen = true;
    },
    async fetchAll() {
      this.readings = [];
      this.forecast = null;
      this.loading = true;
      try {
        await Promise.all([this.fetchForecast(), this.fetchSnowReading()]);
      } catch (error) {
        console.warn(error);
      }
      this.$nextTick(() => {
        this.loading = false;
        this.setLocationOpen = false;
      });
    },
    async fetchForecast() {
      const response = await axios.get("/api/snowForecast", {
        params: {
          latitude: this.latLng.latitude,
          longitude: this.latLng.longitude
        }
      });
      this.forecast = response.data;
    },
    async fetchSnowReading() {
      const response = await axios.get("/api/snow", {
        params: {
          latitude: this.latLng.latitude,
          longitude: this.latLng.longitude
        }
      });
      this.readings = response.data;
    },
    markerMove(lngLat) {
      this.location = {
        name: "Map location",
        point: {
          coordinates: [lngLat.lat, lngLat.lng]
        }
      };
    }
  },
  watch: {
    location() {
      this.updateRoute();
      this.fetchAll();
    },
    $route(to, from) {
      // route name changes are already handled
      if (from.name !== to.name) {
        return;
      }
      this.updateLocationFromRoute(to, true);
    }
  },
  created() {
    if (this.$route.query.lat && this.$route.query.lng) {
      this.updateLocationFromRoute(this.$route);
    } else if (this.$store.getters.hasLocation) {
      const { location } = this.$store.state;
      this.location = {
        name: "Current location",
        point: {
          coordinates: [location.latitude, location.longitude]
        }
      };
      this.fetchAll();
    }
  }
};
</script>
